import { useState, useEffect } from "react";
import { useGetIdentity } from "ra-core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Layers,
  Coins,
  ShieldCheck,
  Bot,
  Building2,
  PhoneCall,
  Zap,
  Globe,
  Compass,
  FileText,
  Users,
  Share2,
  ArrowRight,
  Calendar,
} from "lucide-react";

export interface UpdateItem {
  id: string;
  icon: any;
  iconBg: string;
  iconColor: string;
  title: string;
  badge: string;
  description: string;
}

export interface ReleaseBatch {
  id: string;
  version: string;
  title: string;
  date: string;
  isLatest?: boolean;
  updates: UpdateItem[];
}

export const ALL_RELEASES: ReleaseBatch[] = [
  {
    id: "release-2-6",
    version: "v2.6",
    title: "Five Lens Psychometrics, AI Proposal Engine & Org Stakeholder Matrix",
    date: "November 2026",
    isLatest: true,
    updates: [
      {
        id: "five-lens-matrix",
        icon: Compass,
        iconBg: "bg-indigo-500/10",
        iconColor: "text-indigo-600 dark:text-indigo-400",
        title: "Five Lens Enneagram™ Behavioral Matrix Card",
        badge: "Psychometrics",
        description:
          "Contact profiles now feature an interactive Five Lens matrix tracking Enneagram Core Type & Wings, Instinctual Subtypes, 5 Core Dimensional Sliders (Energy, Social, Cognitive, Action, Resonance), and AI executive coaching debrief hooks.",
      },
      {
        id: "ai-proposal-engine",
        icon: FileText,
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        title: "1-Click AI Proposal & Statement of Work Generator",
        badge: "Sales & SOW",
        description:
          "Open any deal to instantly generate an institutional Statement of Work. Automatically customized for Five Lens, LearnSmartly, or Ennea World with structured phases, governance covenants, and exact commercial pricing in ZAR, GBP, or USD.",
      },
      {
        id: "stakeholder-org-map",
        icon: Users,
        iconBg: "bg-purple-500/10",
        iconColor: "text-purple-600 dark:text-purple-400",
        title: "Account Stakeholder Penetration & Org Chart",
        badge: "Account Mapping",
        description:
          "Companies now feature a Stakeholders & Org Map tab categorizing contacts into Executive Sponsors (Budget Sign-Off), Strategic Decision Makers, Internal Champions, and Cohort Learners.",
      },
      {
        id: "kanban-normalizer",
        icon: Coins,
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-600 dark:text-amber-400",
        title: "Multi-Brand Kanban Filter & Currency Normalizer",
        badge: "Pipeline",
        description:
          "Filter the Deals pipeline by Five Lens, LearnSmartly, or Ennea World. Toggle between native currency amounts and unified benchmark totals in USD, ZAR, or GBP with real-time conversion estimates.",
      },
      {
        id: "whatsapp-win-share",
        icon: Share2,
        iconBg: "bg-teal-500/10",
        iconColor: "text-teal-600 dark:text-teal-400",
        title: "1-Click WhatsApp & Team Win Sharing",
        badge: "Collaboration",
        description:
          "Share deal updates and winning proposals directly to team WhatsApp or Slack groups with 1 click, formatted with executive details and direct CRM links.",
      },
    ],
  },
  {
    id: "release-2-5",
    version: "v2.5",
    title: "Multi-Brand Group Entities, Multi-Currency & Duplicate Radar",
    date: "Early November 2026",
    updates: [
      {
        id: "multi-brand",
        icon: Layers,
        iconBg: "bg-indigo-500/10",
        iconColor: "text-indigo-600 dark:text-indigo-400",
        title: "Multi-Brand Group Entity Selector",
        badge: "Brand Routing",
        description:
          "Assign contacts, companies, or deals to Five Lens Enneagram, LearnSmartly Institute, or Ennea World with tailored AI outreach sequences.",
      },
      {
        id: "multi-currency",
        icon: Coins,
        iconBg: "bg-emerald-500/10",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        title: "Multi-Currency Deals (ZAR, GBP & USD)",
        badge: "Kanban & Pipeline",
        description:
          "Deals can now be tracked in South African Rand (R), British Pounds (£), or US Dollars ($) across all deal stages and dashboards.",
      },
      {
        id: "duplicate-radar",
        icon: ShieldCheck,
        iconBg: "bg-amber-500/10",
        iconColor: "text-amber-600 dark:text-amber-400",
        title: "Multi-User Duplicate Prevention Radar",
        badge: "Team Integrity",
        description:
          "Real-time duplicate detection as you type companies, website domains, emails, or LinkedIn profiles. Surfaces existing account owners to keep data unified.",
      },
    ],
  },
  {
    id: "release-2-4",
    version: "v2.4",
    title: "AI Outbound Signals, Champion Tracking & Meeting Summarizer",
    date: "Late October 2026",
    updates: [
      {
        id: "signal-command-center",
        icon: Bot,
        iconBg: "bg-sky-500/10",
        iconColor: "text-sky-600 dark:text-sky-400",
        title: "AI LinkedIn Outbound & Signal Command Center",
        badge: "AI Outbound",
        description:
          "Live dashboard tracking ICP scores, job-change signals, active DM conversations, and 1-click personalized LinkedIn engagement hooks.",
      },
      {
        id: "meeting-summarizer",
        icon: PhoneCall,
        iconBg: "bg-teal-500/10",
        iconColor: "text-teal-600 dark:text-teal-400",
        title: "AI Meeting Note Summarizer & Task Scheduler",
        badge: "Call Intelligence",
        description:
          "Paste rough call notes or Zoom transcripts into any contact note; Gemini extracts executive summaries and queues follow-up tasks into the CRM agenda.",
      },
    ],
  },
];

export const CURRENT_RELEASE_VERSION = "2026.11-v2.6-enterprise-intelligence";

export interface WhatsNewModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WhatsNewModal = ({ open: controlledOpen, onOpenChange }: WhatsNewModalProps) => {
  const { identity, isLoading } = useGetIdentity();
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const [dontShowAgain, setDontShowAgain] = useState(true);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = (nextOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(nextOpen);
    } else {
      setInternalOpen(nextOpen);
    }
  };

  useEffect(() => {
    if (isLoading || !identity) return;
    const storageKey = `ennea_crm_release_seen_${CURRENT_RELEASE_VERSION}_${identity.id}`;
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [identity, isLoading]);

  const handleClose = () => {
    if (identity && dontShowAgain) {
      const storageKey = `ennea_crm_release_seen_${CURRENT_RELEASE_VERSION}_${identity.id}`;
      localStorage.setItem(storageKey, "true");
    }
    setIsOpen(false);
  };

  const totalUpdatesCount = ALL_RELEASES.reduce((acc, r) => acc + r.updates.length, 0);

  const filteredReleases =
    selectedTab === "all"
      ? ALL_RELEASES
      : ALL_RELEASES.filter((r) => r.id === selectedTab);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0 border-border/60 shadow-2xl rounded-2xl"
        showCloseButton
      >
        {/* Banner Header */}
        <div className="relative p-6 bg-gradient-to-br from-indigo-950 via-slate-900 to-amber-950 text-white rounded-t-2xl overflow-hidden border-b border-white/10">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Sparkles className="w-36 h-36 text-amber-300" />
          </div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border-amber-400/30 text-xs gap-1 py-0.5 px-2">
              <Sparkles className="w-3 h-3 fill-amber-300" />
              Consolidated Updates Hub
            </Badge>
            <span className="text-xs text-slate-300 font-medium">
              {ALL_RELEASES.length} Releases • {totalUpdatesCount} Upgrades
            </span>
          </div>
          <DialogTitle className="text-2xl font-bold text-white tracking-tight">
            What's New in Ennea International CRM
          </DialogTitle>
          <DialogDescription className="text-slate-300 text-xs sm:text-sm mt-1 max-w-lg">
            Five Lens psychometrics, AI proposal & SOW engine, account org maps, multi-brand filtering, and currency normalizer.
          </DialogDescription>

          {/* Release Filter Tabs */}
          <div className="flex items-center gap-1.5 mt-4 overflow-x-auto pb-1 scrollbar-none">
            <button
              type="button"
              onClick={() => setSelectedTab("all")}
              className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all shrink-0 ${
                selectedTab === "all"
                  ? "bg-amber-400 text-slate-950 shadow-sm font-bold"
                  : "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
              }`}
            >
              All Updates ({totalUpdatesCount})
            </button>
            {ALL_RELEASES.map((rel) => (
              <button
                key={rel.id}
                type="button"
                onClick={() => setSelectedTab(rel.id)}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all shrink-0 flex items-center gap-1 ${
                  selectedTab === rel.id
                    ? "bg-amber-400 text-slate-950 shadow-sm font-bold"
                    : "bg-white/10 text-white/80 hover:bg-white/20 hover:text-white"
                }`}
              >
                <span>{rel.version}</span>
                {rel.isLatest && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Feature List (Combined / Grouped by Release) */}
        <div className="p-6 space-y-6 bg-background">
          {filteredReleases.map((release) => (
            <div key={release.id} className="space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-foreground">
                    {release.version} — {release.title}
                  </span>
                  {release.isLatest && (
                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-[10px] py-0 px-1.5">
                      Latest
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  {release.date}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {release.updates.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.id}
                      className="flex items-start gap-3.5 p-3 rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors"
                    >
                      <div
                        className={`p-2 rounded-lg ${item.iconBg} ${item.iconColor} shrink-0 mt-0.5`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-xs sm:text-sm text-foreground">
                            {item.title}
                          </h4>
                          <Badge
                            variant="secondary"
                            className="text-[10px] py-0 px-1.5 h-4"
                          >
                            {item.badge}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Modal Footer */}
        <div className="p-4 px-6 bg-muted/40 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-border text-primary focus:ring-primary w-4 h-4"
            />
            Mark all updates as seen (don't pop up again)
          </label>

          <Button
            onClick={handleClose}
            className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-5 h-9 text-xs gap-1.5 shadow-sm"
          >
            Got It, Let's Explore
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
